import React, { useState, useEffect } from 'react';
import { withSize } from 'react-sizeme';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';

import {
    FormComputed,
    HiddenField,
    LegacyCityPropType,
    PROVINCE,
    CircleSpinner,
    MessagePropType,
    Config,
} from '@ratehub/base-ui';
import { PAYMENT_FREQUENCIES, PURPOSES } from '@ratehub/documents/mtg-journey-document/stable';
import { SCENARIO_TYPES } from '@ratehub/mtg-common';

import { MONTHLY_EXPENSES } from '../definitions/MonthlyExpensesDefaultValues';
import getScenarioCount from '../functions/getScenarioCount';
import PaymentCalculatorScenarioStep from './PaymentCalculatorScenarioStep';
import PaymentCalculatorStepCashNeeded from './PaymentCalculatorStepCashNeeded';
import PaymentCalculatorStepMonthlyExpenses from './PaymentCalculatorStepMonthlyExpenses';
import PaymentCalculatorStepAmortizationSchedule from './PaymentCalculatorStepAmortizationSchedule';
import PaymentCalculatorStepInterestRateRisk from './PaymentCalculatorStepInterestRateRisk';
import MortgageCalculator from './MortgageCalculator';


function PaymentCalculator({
    scenario,
    cashNeededIntroduction,
    monthlyExpenseIntroduction,
    initialAskingPrice,
    initialAnnualPropertyTax,
    initialProvinceCode,
    initialCity,
    preferredProvider,
    isProductReferralDisabled,
    size,
    ...otherProps
}) {
    const [ form, setForm ] = useState(null);

    const scenarioCount = getScenarioCount(size?.width);
    const isReady = scenarioCount && form?.values.scenarios;

    useEffect(() => {
        // Updates the list of options based on active scenarios
        if (form == null) {
            return;
        }

        form.setValue('size', size);

        // If the option has been removed based on screen size, reset the default value
        if (form.values.selectedScenarioNumber > scenarioCount) {
            form.setValue('selectedScenarioNumber', 1);
        }
    }, [ form, scenarioCount, size ]);

    function handleChangeScenario(newScenario) {
        form.setValue('selectedScenarioNumber', newScenario);
    }

    return (
        <StyledCalculator
            title={(
                <FormattedMessage
                    id="mtg-calculator.PaymentCalculator.calculatorTitle"
                    defaultMessage="Ratehub.ca's {scenario, select,
                            renewal {renewal}
                            other {}
                        } mortgage payment calculator"
                    values={{ scenario: scenario }}
                />
            )}
            purpose={scenario === SCENARIO_TYPES.PURCHASE ? PURPOSES.PURCHASE_FOUND_PROPERTY : scenario}
            isSaveEnabled={Config.ENABLE_SAVING_DATA_FOR_CALCULATORS}
            onSaveDocument={saveFormData}
            onCreateFormState={setForm}
            data-test-name="payment-calculator"
            {...otherProps}
        >
            {/* Spin until sizeme is ready */}
            <If condition={!size.width}>
                <CircleSpinner />
            </If>

            <FormComputed
                name="scenarioType"
                value={scenario}
            />

            <HiddenField
                name="selectedScenarioNumber"
                value={1}
            />

            <HiddenField
                name="size"
                value={size}
            />

            <If condition={scenarioCount}>
                <PaymentCalculatorScenarioStep
                    stepNumber={1}
                    scenarioCount={scenarioCount}
                    initialAskingPrice={initialAskingPrice}
                    initialProvinceCode={initialProvinceCode}
                    initialCity={initialCity}
                    preferredProvider={preferredProvider}
                    isProductReferralDisabled={isProductReferralDisabled}
                />

                <If condition={isReady}>
                    <Choose>
                        <When condition={scenario === SCENARIO_TYPES.PURCHASE}>
                            <PaymentCalculatorStepCashNeeded
                                scenarioType={scenario}
                                stepNumber={2}
                                scenarioCount={scenarioCount}
                                introduction={cashNeededIntroduction}
                                onChangeScenario={handleChangeScenario}
                            />

                            <PaymentCalculatorStepMonthlyExpenses
                                scenarioType={scenario}
                                stepNumber={3}
                                annualPropertyTax={initialAnnualPropertyTax}
                                scenarioCount={scenarioCount}
                                introduction={monthlyExpenseIntroduction}
                                onChangeScenario={handleChangeScenario}
                            />

                            <PaymentCalculatorStepInterestRateRisk
                                scenarioType={scenario}
                                stepNumber={4}
                                scenarioCount={scenarioCount}
                                onChangeScenario={handleChangeScenario}
                            />

                            <PaymentCalculatorStepAmortizationSchedule
                                scenarioType={scenario}
                                stepNumber={5}
                                scenarioCount={scenarioCount}
                                onChangeScenario={handleChangeScenario}
                            />
                        </When>
                        <Otherwise>
                            <PaymentCalculatorStepMonthlyExpenses
                                scenarioType={scenario}
                                stepNumber={2}
                                annualPropertyTax={initialAnnualPropertyTax}
                                scenarioCount={scenarioCount}
                                introduction={monthlyExpenseIntroduction}
                                onChangeScenario={handleChangeScenario}
                            />

                            <PaymentCalculatorStepInterestRateRisk
                                scenarioType={scenario}
                                stepNumber={3}
                                scenarioCount={scenarioCount}
                                onChangeScenario={handleChangeScenario}
                            />

                            <PaymentCalculatorStepAmortizationSchedule
                                scenarioType={scenario}
                                stepNumber={4}
                                scenarioCount={scenarioCount}
                                onChangeScenario={handleChangeScenario}
                            />
                        </Otherwise>
                    </Choose>
                </If>
            </If>
        </StyledCalculator>
    );
}

function saveFormData(document, values) {
    if (!document) {
        return;
    }

    // This a work around for saving nested arrays (they're not supported using our update method)
    if ((document.journey?.calculators?.contacts?.length ?? 0) === 0) {
        document?.journey?.update({
            // creating a placeholder contact ahead of time
            calculators: { contacts: [ { applicationRank: 1 } ] },
        });
    }

    if ((document.journey?.calculators?.payment?.mortgages?.length ?? 0) === 0) {
        document?.journey?.update({
            // creating a placeholder contact ahead of time
            calculators: { payment: { mortgages: [] } },
        });
    }

    // NOTE: creating an object with keys as indices so that partiallyUpdateModel
    // will merge/combine the mortgages array instead of overwriting it
    const mortgageOptions = values?.scenarios?.reduce((scenarios, current, idx) => {
        return Object.assign(scenarios, {
            [idx]: {
                type: current?.rateType,
                term: current?.rateTerm,
                amortization: parseInt(current?.amortization) * 12, // document amortization is in months
                downPayment: current?.downPayment?.dollars,
                paymentFrequencyType: current?.paymentFrequency,
            },
        });
    }, {});

    // same as above - creating an object with keys as indices
    const monthlyExpenses = Object.values(MONTHLY_EXPENSES).reduce((expenses, current, idx) => {
        return Object.assign(expenses, {
            [idx]: {
                frequency: PAYMENT_FREQUENCIES.MONTHLY,
                type: current,
                value: values?.[current],
            },
        });
    }, {});

    document.journey?.update({
        calculators: {
            property: {
                homeValue: values?.askingPrice,
                address: {
                    city: values?.location?.name,
                    province: values?.location?.province?.code,
                },
            },
            contacts: {
                0: {
                    expenses: monthlyExpenses,
                },
            },
            firstTimeHomeBuyer: values?.isFirstTimeBuyer,
            payment: {
                lawyerFees: values?.lawyerFee,
                titleInsuranceCost: values?.titleInsurance,
                homeInspectionCost: values?.homeInspection,
                appraisalFees: values?.appraisalFee,
                estoppelFees: values?.estoppelFee,
                mortgages: mortgageOptions,
            },
        },
    });
}

PaymentCalculator.propTypes = {
    scenario: PropTypes.oneOf(Object.values(SCENARIO_TYPES)),
    cashNeededIntroduction: MessagePropType,
    monthlyExpenseIntroduction: MessagePropType,
    initialAskingPrice: PropTypes.number,
    initialAnnualPropertyTax: PropTypes.number,
    initialProvinceCode: PropTypes.oneOf(Object.values(PROVINCE)),
    initialCity: LegacyCityPropType,
    size: PropTypes.object.isRequired,
    preferredProvider: PropTypes.string,
    isProductReferralDisabled: PropTypes.bool,
};

PaymentCalculator.defaultProps = {
    scenario: SCENARIO_TYPES.PURCHASE,
    monthlyExpenseIntroduction: undefined,
    cashNeededIntroduction: undefined,
    initialAskingPrice: undefined,
    initialAnnualPropertyTax: undefined,
    initialProvinceCode: undefined,
    initialCity: undefined,
    preferredProvider: undefined,
    isProductReferralDisabled: false,
};

const StyledCalculator = styled(MortgageCalculator)`
    max-width: 75rem; //1200px
`;

export default withSize({ noPlaceholder: true })(observer(PaymentCalculator));
